import { v4 as uuid } from 'uuid';

import {
    EntryTypeBlockData,
    EntryTypeData,
    EntryTypeFieldData,
    EntryTypeSectionBlockSettingsData,
    EntryTypeSectionData,
    Optional,
} from 'chaika-frontend-types/frontend-cms';
import type { TranslationsDataParams } from 'chaika-types';
import { Locale } from 'chaika-enums';

import { EntryTypeDraft, EntryTypeDraftBlock, EntryTypeDraftField, EntryTypeDraftSection } from '../types';

export function normalizeEntryTypeDraft(entryType: EntryTypeData): EntryTypeDraft {
    return {
        ...entryType,
        titleTranslations: normalizeTranslations(entryType.titleTranslations),
        sections: entryType.sections.map(normalizeSection),
    };
}

export function normalizeTranslations(
    translations: Optional<TranslationsDataParams[]>,
): Optional<TranslationsDataParams[]> {
    return translations ? dedupeTranslations(translations) : translations;
}

export function normalizeBlock(block: EntryTypeBlockData): EntryTypeDraftBlock {
    return { ...block, fields: block.fields.map(normalizeField) };
}

export function dedupeTranslations(translations: TranslationsDataParams[]): TranslationsDataParams[] {
    const byLocale = new Map<Locale, TranslationsDataParams>();

    translations.forEach(translation => byLocale.set(translation.locale, translation));

    return Array.from(byLocale.values());
}

export function findSection(draft: EntryTypeDraft, sectionId: string): Optional<EntryTypeDraftSection> {
    return draft.sections.find(section => section.id === sectionId);
}

export function findSectionByBlockId(draft: EntryTypeDraft, blockId: string): Optional<EntryTypeDraftSection> {
    return draft.sections.find(section => section.blocks.some(block => block.id === blockId));
}

export function findBlock(draft: EntryTypeDraft, sectionId: string, blockId: string): Optional<EntryTypeDraftBlock> {
    return findSection(draft, sectionId)?.blocks.find(block => block.id === blockId);
}

export function findBlockById(draft: EntryTypeDraft, blockId: string): Optional<EntryTypeDraftBlock> {
    return findSectionByBlockId(draft, blockId)?.blocks.find(block => block.id === blockId);
}

export function findField(
    draft: EntryTypeDraft,
    sectionId: string,
    blockId: string,
    fieldName: string,
): Optional<EntryTypeDraftField> {
    return findBlock(draft, sectionId, blockId)?.fields.find(field => field.name === fieldName);
}

export function getSectionPosition(draft: EntryTypeDraft, sectionId: string): number {
    return draft.sections.findIndex(section => section.id === sectionId);
}

export function getBlockPosition(draft: EntryTypeDraft, blockId: string): Optional<number> {
    return findSectionByBlockId(draft, blockId)?.blocks.findIndex(block => block.id === blockId);
}

export function getBlockIds(draft: EntryTypeDraft): string[] {
    return draft.sections.flatMap(section => section.blocks.map(block => block.id));
}

export function getBlockSettings(draft: EntryTypeDraft, blockId: string): Optional<EntryTypeSectionBlockSettingsData> {
    return findSectionByBlockId(draft, blockId)?.settings?.blockSettings?.[blockId];
}

function normalizeSection(section: EntryTypeSectionData): EntryTypeDraftSection {
    return {
        ...section,
        id: section.id ?? uuid(),
        collapsible: section.collapsible ?? true,
        titleTranslations: normalizeTranslations(section.titleTranslations),
        blocks: section.blocks.map(normalizeBlock),
    };
}

function normalizeField(field: EntryTypeFieldData): EntryTypeDraftField {
    return { ...field, data: field.data ?? {} };
}
